import React from 'react';
import SEO from '../components/SEO';
import { SITE_URL, SITE_NAME, CONTACT_EMAIL } from '../config/site';
import './LegalPage.css';

const PrivacyPolicyPage = () => (
  <div className="legal-page">
    <SEO
      title={`Privacy Policy | ${SITE_NAME}`}
      description="Privacy policy for Dr. Muyabins Dental Care. Learn how we collect, use, and protect your information, including cookies and advertising."
      url={`${SITE_URL}/privacy`}
    />
    <div className="legal-container">
      <h1>Privacy Policy</h1>
      <p className="legal-updated">Last updated: June 12, 2026</p>

      <section>
        <h2>Introduction</h2>
        <p>
          {SITE_NAME} (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) operates {SITE_URL}.
          This Privacy Policy explains how we collect, use, disclose, and safeguard your
          information when you visit our website, book appointments, contact us, subscribe to
          our newsletter, or leave comments on our blog.
        </p>
      </section>

      <section>
        <h2>Information We Collect</h2>
        <h3>Information you provide</h3>
        <ul>
          <li>Name, email address, and phone number (appointment and contact forms)</li>
          <li>Messages and notes you submit through our forms</li>
          <li>Email address (newsletter subscription)</li>
          <li>Name and comment content (blog comments)</li>
        </ul>
        <h3>Information collected automatically</h3>
        <ul>
          <li>Device type, browser, and general usage data via Google Analytics</li>
          <li>IP address and pages visited (standard server and analytics logs)</li>
          <li>Cookies and similar technologies (see Cookies section below)</li>
        </ul>
      </section>

      <section>
        <h2>How We Use Your Information</h2>
        <ul>
          <li>Respond to inquiries and confirm appointments</li>
          <li>Send newsletter updates (with your consent)</li>
          <li>Moderate and display blog comments</li>
          <li>Improve our website and understand visitor traffic</li>
          <li>Display advertisements on our blog through Google AdSense</li>
        </ul>
      </section>

      <section>
        <h2>Cookies and Advertising</h2>
        <p>
          We use cookies and similar tracking technologies to analyze site traffic and,
          on our blog pages, to serve advertisements through{' '}
          <strong>Google AdSense</strong>, a third-party advertising service provided by Google LLC.
        </p>
        <p>
          Google uses cookies to serve ads based on your prior visits to this website or
          other websites. You may opt out of personalized advertising by visiting{' '}
          <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer">
            Google Ads Settings
          </a>
          . You can also opt out of third-party vendor cookies at{' '}
          <a href="https://optout.aboutads.info/" target="_blank" rel="noopener noreferrer">
            aboutads.info
          </a>
          .
        </p>
        <p>
          For more information on how Google uses data from partner sites, see{' '}
          <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer">
            How Google uses information from sites or apps that use our services
          </a>{' '}
          and{' '}
          <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer">
            Google&apos;s Advertising Policies
          </a>
          .
        </p>
      </section>

      <section>
        <h2>Third-Party Services</h2>
        <p>We use the following third-party services that may process your data:</p>
        <ul>
          <li><strong>Google Analytics</strong> — website traffic analysis</li>
          <li><strong>Google AdSense</strong> — advertising on blog pages</li>
          <li><strong>Firebase (Google)</strong> — hosting data, forms, and blog content</li>
        </ul>
        <p>Each provider has its own privacy policy governing their use of your data.</p>
      </section>

      <section>
        <h2>Data Retention and Security</h2>
        <p>
          We retain your information only as long as needed to fulfill the purposes described
          in this policy or as required by law. We use reasonable technical and organizational
          measures to protect your data, though no method of transmission over the Internet is
          100% secure.
        </p>
      </section>

      <section>
        <h2>Your Rights</h2>
        <p>
          Depending on your location, you may have the right to access, correct, or delete
          personal information we hold about you. To make a request, contact us at{' '}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </p>
      </section>

      <section>
        <h2>Children&apos;s Privacy</h2>
        <p>
          Our website is not directed at children under 13. We do not knowingly collect
          personal information from children under 13.
        </p>
      </section>

      <section>
        <h2>Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. Changes will be posted on
          this page with an updated revision date.
        </p>
      </section>

      <section>
        <h2>Contact Us</h2>
        <p>
          If you have questions about this Privacy Policy, contact us at{' '}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> or through our{' '}
          <a href="/contact">contact page</a>.
        </p>
      </section>
    </div>
  </div>
);

export default PrivacyPolicyPage;
